import styles from "./reviewSharedData.module.scss";
import { useSelector, useDispatch } from "react-redux";
import { deleteComment, addComment } from "./reviewSharedDataSlice";
import sharedDataApi from "../../api/sharedDataApi";
import { getDateString } from "../../utilities/helperMethods";
import { useState } from "react";
import { v4 } from "uuid";
import { selectUser } from "../../app/appSlice";
import { useSearchParams } from "react-router-dom";

// CommentDisplayBox component displays each comment with reply and delete options
const CommentDisplayBox = ({
  comment,
  onReplyClick,
  onDeleteClick,
  isReplyVisible,
  replyText,
  onReplyChange,
  onReplySubmit,
  onCancel,
  disableAddComment,
  hasChildren,
  user,
  actionItemDetails,
  tab,
}) => (
  <div
    className={`${styles.commentDisplayBox} ${
      comment.parent_comment_id ? styles.childComment : ""
    }`}
  >
    <div className={styles.commentText}>
      <p className={styles.author}>
        <span className={styles.avatar}>
          {comment.name ? comment.name[0] : ""}
        </span>
        <span className={styles.commentAuthorNameDate}>
          <strong>{comment.name || "Anonymous"}</strong>
          <small className={styles.commentDate}>
            <span>Date:</span> {getDateString(new Date(comment.date))}
          </small>
        </span>
      </p>
      <p>
        <span className={styles.commentText}>{comment.text}</span>
      </p>
    </div>
    <div className={styles.commentActions}>
        <button
        className={`${styles.actions} ${
          !(["In_Review", "Staging", "Pre_Prod"].includes(actionItemDetails?.State) &&
            (user.email === actionItemDetails?.CreatedByEmail || actionItemDetails?.ReviewerEmails?.includes(user.email)))
            ? styles.actionsDisable
            : ""
        }`}
        onClick={() => onReplyClick(comment)}
        disabled={!
          (["In_Review", "Staging", "Pre_Prod"].includes(actionItemDetails?.State) &&
            (user.email === actionItemDetails?.CreatedByEmail || actionItemDetails?.ReviewerEmails?.includes(user.email)))
        }
      >
        Reply
      </button>
      <button
        className={`${styles.actions} ${
          hasChildren || comment.name !== user.email || actionItemDetails?.State === "Production"
            ? styles.actionsDisable
            : ""
        }`}
        onClick={() => onDeleteClick(comment)}
        disabled={hasChildren || comment.name !== user.email || actionItemDetails?.State === "Production"}
      >
        Delete
      </button>
    </div>

    {isReplyVisible && (
      <ReplyForm
        replyText={replyText}
        onReplyChange={onReplyChange}
        onSubmit={() => onReplySubmit(comment)}
        onCancel={onCancel}
        disableAddComment={disableAddComment}
      />
    )}
  </div>
);

// ReplyForm component handles the UI for replying to a comment
const ReplyForm = ({
  replyText,
  onReplyChange,
  onSubmit,
  onCancel,
  disableAddComment,
}) => (
  <div className={styles.replyForm}>
    <textarea
      value={replyText}
      onChange={onReplyChange}
      placeholder="Write your reply..."
      className={styles.replyTextarea}
    />
    <div className={styles.commentActions}>
      <button
        className={styles.actions}
        onClick={onSubmit}
        disabled={!replyText || disableAddComment}
      >
        Submit
      </button>
      <button className={styles.actions} onClick={onCancel}>
        Cancel
      </button>
    </div>
  </div>
);

// RenderComments component displays all comments and their replies, handles delete and reply actions
const RenderComments = ({
  comments = [],
  label,
  parent_comment_id = null,
  onDeleteComment,
  onAddReply,
}) => {
  const dispatch = useDispatch();
  const [replyingTo, setReplyingTo] = useState({}); // Track the comment being replied to
  const [replyText, setReplyText] = useState(""); // Track the reply text
  const [isTextBoxVisible, setIsTextBoxVisible] = useState(false); // Control reply textbox visibility
  const [disableAddComment, setDisableAddComment] = useState(false); // Disable add comment button during submission

  const user = useSelector(selectUser);
  const actionItemDetails = useSelector(
    (state) => state.reviewSharedData.actionItemData
  );

  const [searchParam, _] = useSearchParams();
  const tab = searchParam.get("tab") || "incoming";

  // Check if a comment has child comments
  const hasChildComments = (commentId) => {
    return comments.some((child) => child.parent_comment_id === commentId);
  };

  // Handle comment deletion
  const handleDeleteComment = async (comment) => {
    try {
      if (onDeleteComment) {
        onDeleteComment(comment);
      } else {
        await sharedDataApi.deleteComment({
          input: { commentId: comment.commentId, date: new Date(comment.date) },
        });
        dispatch(deleteComment(comment));
      }
    } catch (error) {
      console.error("Error deleting comment:", error);
    }
  };

  // Open reply box when 'Reply' button is clicked
  const handleReplyClick = (comment) => {
    setReplyingTo(comment);
    setIsTextBoxVisible(true);
  };

  // Update reply text state on input change
  const handleReplyChange = (e) => {
    setReplyText(e.target.value);
  };

  // Submit reply to the server or dispatch action to Redux
  const handleReplySubmit = async (comment) => {
    const actionId = searchParam.get("actionId");
    setDisableAddComment(true); // Disable submit to prevent duplicate submissions
    const payload = {
      commentId: v4(),
      name: user.email,
      date: new Date().toString(),
      text: replyText,
      parent_comment_id: comment.parent_comment_id || comment.commentId,
      property_name: label,
      actionId: actionId || actionItemDetails.id,
    };
    try {
      if (onAddReply) {
        onAddReply(payload);
      } else {
        await sharedDataApi.createComment({
          input: { ...payload, date: new Date(payload.date) },
        });
        dispatch(addComment(payload));
      }
      handleCancel();
    } catch (error) {
      console.error("Error submitting reply:", error);
    }
    setDisableAddComment(false);
  };

  // Reset reply box state when 'Cancel' is clicked
  const handleCancel = () => {
    setIsTextBoxVisible(false);
    setReplyingTo({});
    setReplyText("");
  };

  return (
    // Container for scrollable list of comments
    <div className={styles.scrollableComments}>
      {comments
        // Filter to display only top-level comments (those with no parent)
        .filter(
          (comment) =>
            comment.parent_comment_id === null &&
            comment.property_name === label
        )
        .map((parent) => (
          // Render each top-level comment
          <div key={parent.commentId}>
            <CommentDisplayBox
              comment={parent}
              onReplyClick={handleReplyClick}
              onDeleteClick={handleDeleteComment}
              isReplyVisible={
                replyingTo.commentId === parent.commentId && isTextBoxVisible
              }
              replyText={replyText}
              onReplyChange={handleReplyChange}
              onReplySubmit={handleReplySubmit}
              onCancel={handleCancel}
              disableAddComment={disableAddComment}
              hasChildren={hasChildComments(parent.commentId)}
              user={user}
               actionItemDetails={actionItemDetails}
               tab={tab}
            />
            {comments
              // Filter to find child comments related to this parent comment
              .filter((child) => child.parent_comment_id === parent.commentId)
              .map((child) => (
                // Render each child comment with appropriate handlers and props
                <CommentDisplayBox
                  key={child.commentId}
                  comment={child}
                  onReplyClick={handleReplyClick}
                  onDeleteClick={handleDeleteComment}
                  isReplyVisible={
                    replyingTo.commentId === child.commentId && isTextBoxVisible
                  }
                  replyText={replyText}
                  onReplyChange={handleReplyChange}
                  onReplySubmit={handleReplySubmit}
                  onCancel={handleCancel}
                  disableAddComment={disableAddComment}
                  hasChildren={false}
                  user={user}
                  actionItemDetails={actionItemDetails}
                  tab={tab}
                />
              ))}
          </div>
        ))}
    </div>
  );
};

export default RenderComments;
